import { URLS } from '../constant/url-schema.constant';
import { Is } from '../utils/is';
import {
  hasStored,
  readStored,
  readStoredObject,
  removeStored,
  writeStored,
} from '../utils/storage';

export const PAGE_SIZE_LS_KEY = 'pageSize';

/**
 * Pages storing their own page size, keyed by their url. Each belongs to a widget also displayable
 * in a dashboard, where the size of its configuration wins over the stored one.
 */
export const PageSizeScope = {
  GLOBAL: 'global',
  STATES_CURRENT: `/${URLS.MONITORING}`,
  STATES_SCORING: `/${URLS.SCORING}`,
  INVESTIGATE_TOOL: `/${URLS.MEASURES}`,
} as const;

export type PageSizeScope = (typeof PageSizeScope)[keyof typeof PageSizeScope];

export type PageSizeStorage = Partial<Record<PageSizeScope, number>>;

const LEGACY_KEYS: Record<string, PageSizeScope> = {
  LS_STATE_CURRENT_PAGE_SIZE: PageSizeScope.STATES_CURRENT,
  LS_STATE_SCORING_PAGE_SIZE: PageSizeScope.STATES_SCORING,
  INVESTIGATE_TOOL_PAGE_SIZE: PageSizeScope.INVESTIGATE_TOOL,
};

export function getStoredPageSize(scope: PageSizeScope): number {
  return readStorage()[scope];
}

export function setStoredPageSize(scope: PageSizeScope, pageSize: number): void {
  writeStorage({ ...readStorage(), [scope]: pageSize });
}

/** Folds the legacy flat entries into the `pageSize` entry, then drops them. */
export function migrateLegacyPageSizeStorage(): void {
  const storage = readStorage();
  let hasLegacyEntry = false;

  const legacyGlobal = readStored(PAGE_SIZE_LS_KEY);
  if (Is.number(legacyGlobal)) {
    storage[PageSizeScope.GLOBAL] = Number(legacyGlobal);
    hasLegacyEntry = true;
  }

  for (const [key, scope] of Object.entries(LEGACY_KEYS)) {
    if (!hasStored(key)) {
      continue;
    }

    const pageSize = readStored(key);
    if (Is.number(pageSize)) {
      storage[scope] = Number(pageSize);
    }
    removeStored(key);
    hasLegacyEntry = true;
  }

  if (hasLegacyEntry) {
    writeStorage(storage);
  }
}

/** Guards against the legacy raw number stored under the same key. */
function readStorage(): PageSizeStorage {
  return readStoredObject<PageSizeStorage>(PAGE_SIZE_LS_KEY);
}

function writeStorage(storage: PageSizeStorage): void {
  writeStored(PAGE_SIZE_LS_KEY, storage);
}
